import { useState, useEffect, useRef } from "react";

function DownloadAppButton({ variant = "default" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [platform, setPlatform] = useState("unknown");
  const [isInstalling, setIsInstalling] = useState(false);
  const promptRef = useRef(null);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform("ios");
    } else if (/android/i.test(userAgent)) {
      setPlatform("android");
    } else {
      setPlatform("desktop");
    }

    // Check if already installed (PWA running in standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt (Android/Desktop Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      promptRef.current = e;
      console.log("Install prompt captured and ready");
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Listen for successful install
    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      promptRef.current = null;
      setIsInstalling(false);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const handleInstallClick = async () => {
    // iOS: Apple REQUIRES manual install via Safari's Add to Home Screen
    // This is a platform limitation - no workaround exists
    if (platform === "ios") {
      setShowInstructions(true);
      return;
    }

    // Android/Desktop: One-tap install using the captured prompt
    const prompt = deferredPrompt || promptRef.current;
    
    if (prompt) {
      setIsInstalling(true);
      
      try {
        // Trigger the native install prompt
        prompt.prompt();
        
        // Wait for user's choice
        const { outcome } = await prompt.userChoice;
        
        if (outcome === "accepted") {
          setIsInstalled(true);
        }
        
        // Clear the prompt - can only be used once
        setDeferredPrompt(null);
        promptRef.current = null;
      } catch (err) {
        console.error("Install error:", err);
        // Show fallback instructions only if prompt fails
        setShowInstructions(true);
      } finally {
        setIsInstalling(false);
      }
    } else {
      // No prompt available yet - this can happen if:
      // 1. Browser doesn't support PWA install
      // 2. Site doesn't meet PWA criteria
      // 3. User already dismissed the prompt
      // 4. Prompt hasn't fired yet (rare)
      setShowInstructions(true);
    }
  };

  // Already installed - show badge
  if (isInstalled) {
    return (
      <div className={`download-btn-container ${variant}`}>
        <div className="installed-badge" data-testid="app-installed-badge">
          <span className="badge-icon">✓</span>
          <span>App Installed</span>
        </div>
      </div>
    );
  }

  // Get platform-specific button content
  const getButtonContent = () => {
    if (isInstalling) {
      return { icon: "⏳", text: "Installing...", subtitle: "Please wait" };
    }
    
    const hasPrompt = deferredPrompt || promptRef.current;
    
    switch (platform) {
      case "ios":
        return { icon: "📱", text: "Install App", subtitle: "iPhone / iPad" };
      case "android":
        return { 
          icon: "📲", 
          text: hasPrompt ? "Install Now" : "Install App", 
          subtitle: "One Tap Install" 
        };
      default:
        return { 
          icon: "💻", 
          text: hasPrompt ? "Install Now" : "Install App", 
          subtitle: "Desktop App" 
        };
    }
  };

  const buttonContent = getButtonContent();

  return (
    <div className={`download-btn-container ${variant}`}>
      <button 
        onClick={handleInstallClick}
        className={`download-btn ${variant} ${isInstalling ? 'installing' : ''}`}
        data-testid="download-app-btn"
        disabled={isInstalling}
      >
        <span className="btn-icon">{buttonContent.icon}</span>
        <span className="btn-text">{buttonContent.text}</span>
        <span className="btn-subtitle">{buttonContent.subtitle}</span>
      </button>

      {/* Instructions Modal - Only shown when one-tap isn't available */}
      {showInstructions && (
        <div className="install-modal-overlay" onClick={() => setShowInstructions(false)}>
          <div className="install-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowInstructions(false)}
              data-testid="close-install-modal"
            >
              ×
            </button>
            
            {platform === "ios" ? (
              <>
                <h3>📱 Install on iPhone/iPad</h3>
                <p className="modal-subtitle">Quick 3-step install via Safari</p>
                
                <div className="ios-requirement-note">
                  <span>🍎</span>
                  <span>Apple requires PWA apps to be installed through Safari's "Add to Home Screen" feature.</span>
                </div>
                
                <div className="install-steps">
                  <div className="step">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <span className="step-text">Tap the <strong>Share</strong> button <span className="share-icon">⬆️</span></span>
                      <span className="step-hint">Bottom center of Safari</span>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <span className="step-text">Scroll and tap <strong>"Add to Home Screen"</strong></span>
                      <span className="step-hint">Has a ➕ icon next to it</span>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <span className="step-text">Tap <strong>"Add"</strong> in the top right</span>
                      <span className="step-hint">App icon appears on home screen!</span>
                    </div>
                  </div>
                </div>

                <div className="not-safari-note">
                  <span>⚠️</span>
                  <span>Not using Safari? Open this site in Safari first, then follow the steps above.</span>
                </div>
              </>
            ) : platform === "android" ? (
              <>
                <h3>📲 Install on Android</h3>
                <p className="modal-subtitle">Add to your home screen</p>
                
                <div className="install-steps">
                  <div className="step">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <span className="step-text">Tap the <strong>menu (⋮)</strong> button</span>
                      <span className="step-hint">Top-right corner of Chrome</span>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <span className="step-text">Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                      <span className="step-hint">App downloads instantly!</span>
                    </div>
                  </div>
                </div>
                
                <div className="install-tip">
                  <span>💡</span>
                  <span>Look for an install banner at the bottom of your screen - just tap <strong>Install</strong>!</span>
                </div>
              </>
            ) : (
              <>
                <h3>💻 Install Desktop App</h3>
                <p className="modal-subtitle">Get the full app experience</p>
                
                <div className="install-steps">
                  <div className="step">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <span className="step-text">Click the <strong>install icon</strong> in the address bar</span>
                      <span className="step-hint">Or go to Menu → "Install Yisrael Learning Center"</span>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <span className="step-text">Click <strong>"Install"</strong> in the popup</span>
                      <span className="step-hint">App opens in its own window!</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="install-benefits">
              <span>✅</span> 
              <div>
                <strong>What you get:</strong>
                <ul className="benefits-list">
                  <li>Full-screen experience (no browser UI)</li>
                  <li>Home screen / desktop icon</li>
                  <li>Works offline</li>
                  <li>Faster loading times</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DownloadAppButton;
